import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Logo } from '../components/EnergyField';
import { ArchiveDepth, ArchiveDepthBar } from '../components/ArchiveDepth';
import { WeeklyOpenRecord } from '../components/WeeklyOpenRecord';
import { AudioToggle } from '../components/AmbientAudio';
import {
  LogOut, ArrowRight, FileText, Bell, FlaskConical, BookOpen, Bookmark,
  BookmarkCheck, Shield, Download, LayoutGrid, Activity, Radio, Clock, X, Search,
  Lock, Check, Sparkles, CreditCard,
} from 'lucide-react';

import { readTime, statusTag, isExperiment } from '../lib/meta';
import { EvidenceLabel } from '../components/EvidenceLabel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SAVED_KEY = 'fff_saved_logs';
const PENDING_DOMAIN = 'fff_pending_domain';

const TABS = [
  { id: 'logs', label: 'Research Logs', Icon: BookOpen },
  { id: 'vault', label: 'Blueprint Vault', Icon: FileText },
  { id: 'experiments', label: 'Experiments', Icon: FlaskConical },
  { id: 'updates', label: 'Field Updates', Icon: Bell },
  { id: 'saved', label: 'Saved Archive', Icon: Bookmark },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('logs');
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [txOptIn, setTxOptIn] = useState(Boolean(user?.archive_transmissions));
  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SAVED_KEY)) || []; } catch { return []; }
  });

  const applyPendingDomain = (cats) => {
    const pending = localStorage.getItem(PENDING_DOMAIN);
    if (!pending) return;
    localStorage.removeItem(PENDING_DOMAIN);
    const match = cats.find((c) => c.name.toLowerCase().includes(pending.toLowerCase()) || pending.toLowerCase().includes(c.name.toLowerCase().split(' ')[0]));
    if (match) setSelectedCategory(match.id);
  };

  const applyEntry = (cats) => {
    const params = new URLSearchParams(location.search);
    const domain = params.get('domain');
    const tab = params.get('tab');
    let handled = false;
    if (domain) {
      const cat = cats.find((c) => c.id === domain)
        || cats.find((c) => c.name.toLowerCase().includes(domain.toLowerCase()) || domain.toLowerCase().includes(c.name.toLowerCase().split(' ')[0]));
      if (cat) { setSelectedCategory(cat.id); handled = true; }
    }
    if (tab && ['logs', 'vault', 'experiments', 'updates', 'saved'].includes(tab)) { setActiveTab(tab); handled = true; }
    if (handled) {
      localStorage.removeItem(PENDING_DOMAIN);
    } else {
      applyPendingDomain(cats);
    }
  };

  const loadData = async () => {
    try {
      const categoriesRes = await axios.get(`${API}/categories`);
      setCategories(categoriesRes.data);
      if (user?.subscription_status === 'active') {
        const [a, p, u] = await Promise.all([
          axios.get(`${API}/articles`),
          axios.get(`${API}/pdfs`),
          axios.get(`${API}/updates`),
        ]);
        setArticles(a.data); setPdfs(p.data); setUpdates(u.data);
      } else {
        // Free tier — teaser archive (public records full, premium ones locked)
        const [a, p, u, s, w] = await Promise.all([
          axios.get(`${API}/public/articles`),
          axios.get(`${API}/public/pdfs`),
          axios.get(`${API}/public/updates`),
          axios.get(`${API}/public/stats`),
          axios.get(`${API}/public/weekly`),
        ]);
        setArticles(a.data); setPdfs(p.data); setUpdates(u.data); setStats(s.data); setWeekly(w.data);
      }
    } catch (error) {
      if (error.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setProcessingPayment(true);
    const poll = async (attempt = 0) => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          toast.success('Payment confirmed · access granted');
          const u = await axios.get(`${API}/auth/me`);
          setUser(u.data);
          setProcessingPayment(false);
          window.history.replaceState({}, document.title, '/dashboard');
          loadData();
          return;
        }
        if (attempt < 5) { setTimeout(() => poll(attempt + 1), 2000); }
        else { setProcessingPayment(false); window.history.replaceState({}, document.title, '/dashboard'); }
      } catch {
        setProcessingPayment(false);
        window.history.replaceState({}, document.title, '/dashboard');
      }
    };
    poll();
  };

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    void Promise.resolve().then(() => {
      if (sessionId) checkPaymentStatus(sessionId);
      loadData();
    });
  }, [user]);

  // Re-apply tab/domain deep-links whenever the URL query changes (e.g. from the command palette)
  useEffect(() => {
    if (!categories.length) return;
    void Promise.resolve().then(() => { applyEntry(categories); });
  }, [location.search, categories]);

  const handleSubscribe = async () => {
    try {
      const res = await axios.post(`${API}/payments/create-checkout`, { origin_url: window.location.origin });
      window.location.assign(res.data.url);
    } catch { toast.error('Failed to create checkout session'); }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(null);
      toast.success('Session terminated');
      navigate('/');
    } catch { toast.error('Logout failed'); }
  };

  const toggleSave = (id) => {
    setSaved((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(SAVED_KEY, JSON.stringify(next));
      return next;
    });
  };

  const toggleTransmissions = async () => {
    const next = !txOptIn;
    setTxOptIn(next);
    try {
      await axios.put(`${API}/me/preferences`, { archive_transmissions: next });
      toast.success(next ? 'Archive transmissions enabled · a quiet notice when a record surfaces' : 'Archive transmissions disabled');
    } catch {
      setTxOptIn(!next);
      toast.error('Could not update preference');
    }
  };

  const handleRetrieve = async (pdf) => {
    if (pdf.storage_path) {
      try {
        const res = await axios.get(`${API}/pdfs/${pdf.id}/download`, { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch { toast.error('Failed to retrieve blueprint'); }
    } else if (pdf.file_url) {
      window.open(pdf.file_url, '_blank');
    } else {
      toast.error('No file attached to this blueprint');
    }
  };

  const experimentCatId = categories.find((c) => /experiment/i.test(c.name))?.id;
  const byCat = (list) => (selectedCategory ? list.filter((x) => x.category_id === selectedCategory) : list);
  const logs = byCat(articles);
  const experiments = byCat(articles.filter((a) => a.category_id === experimentCatId || isExperiment(a)));
  const savedLogs = articles.filter((a) => saved.includes(a.id));
  const activeCategory = categories.find((c) => c.id === selectedCategory);

  if (loading || processingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#08080A]">
        <div className="text-center space-y-6">
          <div className="spinner mx-auto"></div>
          <p className="mono-label">{processingPayment ? 'Verifying Transaction' : 'Initializing Interface'}</p>
        </div>
      </div>
    );
  }

  // FREE TIER — TEASER ARCHIVE (outer layer of the research network)
  if (user?.subscription_status !== 'active') {
    const teaserTabs = TABS.filter((t) => t.id !== 'saved');
    return (
      <div className="min-h-screen bg-[#08080A] text-[#E2E4E9] overflow-x-hidden">
        <nav className="sticky top-0 z-50 glass-hud border-b border-[#262A36]">
          <div className="container mx-auto px-6 lg:px-12 py-4 flex justify-between items-center gap-4">
            <button onClick={() => navigate('/')} className="shrink-0"><Logo /></button>
            <div className="flex items-center gap-3 min-w-0">
              <AudioToggle className="hidden sm:flex" />
              <span className="tag tag-muted hidden sm:inline">Free Access</span>
              <span className="font-sans-tech text-sm text-[#949BAA] hidden md:inline">{user?.name}</span>
              <button onClick={handleSubscribe} className="btn-ambient !py-2 !px-4 !text-[0.68rem]" data-testid="nav-subscribe-button"><Sparkles className="w-4 h-4" /> Research Access</button>
              <button onClick={handleLogout} className="btn-quiet !py-2 !px-3" data-testid="logout-button"><LogOut className="w-4 h-4" /></button>
            </div>
          </div>
        </nav>

        <div className="container mx-auto px-6 lg:px-12 py-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="mono-label mb-3">Outer Archive Layer · Public Access</p>
            <h1 className="font-serif-display text-4xl lg:text-6xl font-light leading-tight">You are viewing the <span className="gradient-text italic">outer layer</span><br className="hidden sm:block" /> of a deeper research network.</h1>
            <p className="font-sans-tech text-[#949BAA] mt-5 max-w-2xl">Selected records are open. The full archive — blueprints, experiments, measurement logs, and monthly transmissions — sits behind Research Access.</p>
            <ArchiveDepth stats={stats} className="mt-5" />
          </motion.div>

          <ResearchAccessPanel onSubscribe={handleSubscribe} stats={stats} />

          {weekly && (
            <div className="mt-12">
              <WeeklyOpenRecord record={weekly} onOpen={(rid) => navigate(`/article/${rid}`)} />
            </div>
          )}

          <div className="mt-6">
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none" data-testid="transmissions-toggle">
              <input type="checkbox" checked={txOptIn} onChange={toggleTransmissions} className="w-4 h-4 accent-[#1E8F89]" data-testid="transmissions-checkbox" />
              <span className="mono-meta text-[#6B7280]">Receive archive transmissions — a quiet notice when a record surfaces</span>
            </label>
          </div>

          {/* Teaser tabs */}
          <div className="mt-12 border-b border-[#262A36]">
            <div className="flex gap-1 overflow-x-auto pb-px">
              {teaserTabs.map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setActiveTab(id === 'saved' ? 'logs' : id)}
                  className={`flex items-center gap-2 px-4 py-3 font-sans-tech text-sm whitespace-nowrap border-b-2 transition-all ${activeTab === id ? 'border-[#1E8F89] text-[#26B0A8]' : 'border-transparent text-[#949BAA] hover:text-[#E2E4E9]'}`}
                  data-testid={`tab-${id}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Filter chips */}
          {(activeTab === 'logs' || activeTab === 'vault' || activeTab === 'experiments') && (
            <div className="py-6 flex gap-3 overflow-x-auto">
              <button onClick={() => setSelectedCategory(null)} className={`tech-chip ${!selectedCategory ? 'tech-chip-active' : ''}`} data-testid="filter-all"><LayoutGrid className="w-3 h-3 inline mr-1" /> All</button>
              {categories.map((cat) => (
                <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`tech-chip ${selectedCategory === cat.id ? 'tech-chip-active' : ''}`} data-testid={`filter-${cat.name.toLowerCase().replace(/\s/g, '-')}`}>{cat.name}</button>
              ))}
            </div>
          )}

          <div className="py-4 pb-20">
            {(activeTab === 'logs' || activeTab === 'experiments') && (
              <TeaserGrid items={activeTab === 'logs' ? logs : experiments} onOpen={(id) => navigate(`/article/${id}`)} emptyText="No records in this domain yet." />
            )}
            {activeTab === 'vault' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blueprints-grid">
                {byCat(pdfs).length === 0 ? <EmptyState text="No blueprints archived in this domain." /> : byCat(pdfs).map((pdf) => (
                  <div key={pdf.id} className="r-card p-6 flex flex-col relative" data-testid={`blueprint-${pdf.id}`}>
                    <div className="flex justify-between items-start mb-5">
                      <div className="w-11 h-11 rounded-lg border border-[#262A36] bg-[#0c0f17] flex items-center justify-center"><FileText className="w-5 h-5 text-[#6B7280]" /></div>
                      <span className="tag tag-muted flex items-center gap-1"><Lock className="w-3 h-3" /> Restricted</span>
                    </div>
                    <h3 className="font-serif-display text-xl font-medium mb-2">{pdf.title}</h3>
                    <p className="font-sans-tech text-sm text-[#949BAA] leading-relaxed flex-1">{pdf.description}</p>
                    <div className="flex items-center justify-between mt-5">
                      <span className="mono-meta">{pdf.file_size ? `PDF · ${pdf.file_size}` : 'PDF · classified'}</span>
                      <button onClick={handleSubscribe} className="mono-meta flex items-center gap-1.5 text-[#26B0A8] hover:text-[#34d3ca] transition-colors" data-testid={`unlock-${pdf.id}`}><Lock className="w-3 h-3" /> Research Access</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeTab === 'vault' && (
              <p className="mono-meta text-[#5b626f] mt-8 pt-6 border-t border-[#262A36]/60" data-testid="vault-contact-note">
                Trouble accessing a blueprint, or need a specific schematic? Research Contact · <a href="mailto:research@freefieldflow.com" className="text-[#26B0A8] hover:text-[#34d3ca] transition-colors">research@freefieldflow.com</a>
              </p>
            )}
            {activeTab === 'updates' && (
              <div className="max-w-3xl" data-testid="updates-list">
                {updates.length === 0 ? <EmptyState text="No field transmissions logged." /> : (
                  <div className="space-y-8">
                    {updates.map((u) => (
                      <div key={u.id} className="transmission" data-testid={`update-${u.id}`}>
                        <span className="transmission-dot" />
                        <p className="mono-meta text-[#1E8F89] mb-1">{new Date(u.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} · TRANSMISSION{u.locked ? ' · RESTRICTED' : ''}</p>
                        <h3 className="font-serif-display text-2xl font-light">{u.title}</h3>
                        {u.locked ? (
                          <div className="mt-2">
                            <div className="flex items-center gap-2 max-w-md mb-2.5" aria-hidden="true"><span className="redact" style={{ width: '58%' }} /><span className="redact" style={{ width: '22%' }} /></div>
                            <button onClick={handleSubscribe} className="mono-meta flex items-center gap-1.5 text-[#6B7280] hover:text-[#26B0A8] transition-colors" data-testid={`unlock-update-${u.id}`}><Lock className="w-3 h-3" /> Research Access Required to read this transmission</button>
                          </div>
                        ) : (
                          <p className="font-sans-tech text-[#949BAA] mt-2 leading-relaxed">{u.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // CONTROL INTERFACE
  return (
    <div className="min-h-screen bg-[#08080A] text-[#E2E4E9] overflow-x-hidden">
      <nav className="sticky top-0 z-50 glass-hud border-b border-[#262A36]">
        <div className="container mx-auto px-6 lg:px-12 py-4 flex justify-between items-center gap-4">
          <button onClick={() => navigate('/')} className="shrink-0"><Logo /></button>
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden md:block">
              <button onClick={() => window.dispatchEvent(new CustomEvent('fff-open-palette'))} className="btn-quiet !py-2 !px-3 items-center gap-2 flex" data-testid="open-search" aria-label="Open search">
                <Search className="w-4 h-4" /> <span className="mono-meta hidden lg:inline">Search ⌘K</span>
              </button>
            </div>
            <AudioToggle className="hidden sm:flex" />
            <span className="tag tag-teal hidden sm:inline">Active</span>
            {user?.is_admin && <span className="tag tag-steel hidden sm:inline">Admin</span>}
            <span className="font-sans-tech text-sm text-[#949BAA] hidden md:inline">{user?.name}</span>
            {user?.is_admin && (
              <button onClick={() => navigate('/admin')} className="btn-ambient !py-2 !px-4 !text-[0.68rem]" data-testid="admin-panel-link"><Shield className="w-4 h-4" /> Console</button>
            )}
            <button onClick={handleLogout} className="btn-quiet !py-2 !px-3" data-testid="logout-button"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 lg:px-12 py-10 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-8">
        {/* MAIN */}
        <div className="min-w-0">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="mono-label mb-3">Research Control Interface</p>
            <h1 className="font-serif-display text-4xl lg:text-6xl font-light">Welcome, <span className="gradient-text italic">{user?.name?.split(' ')[0]}</span></h1>
          </motion.div>

          {activeCategory && (
            <div className="mt-6 glass-panel rounded-xl px-6 py-4 flex items-center justify-between" data-testid="domain-header">
              <div>
                <p className="mono-meta text-[#1E8F89]">Domain Archive</p>
                <h2 className="font-serif-display text-2xl font-light">{activeCategory.name}</h2>
              </div>
              <button onClick={() => setSelectedCategory(null)} className="text-[#949BAA] hover:text-white transition-colors" data-testid="clear-domain"><X className="w-5 h-5" /></button>
            </div>
          )}

          {/* Tabs */}
          <div className="mt-8 border-b border-[#262A36]">
            <div className="flex gap-1 overflow-x-auto pb-px">
              {TABS.map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 px-4 py-3 font-sans-tech text-sm whitespace-nowrap border-b-2 transition-all ${activeTab === id ? 'border-[#1E8F89] text-[#26B0A8]' : 'border-transparent text-[#949BAA] hover:text-[#E2E4E9]'}`}
                  data-testid={`tab-${id}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Filter chips */}
          {(activeTab === 'logs' || activeTab === 'vault' || activeTab === 'experiments') && (
            <div className="py-6 flex gap-3 overflow-x-auto">
              <button onClick={() => setSelectedCategory(null)} className={`tech-chip ${!selectedCategory ? 'tech-chip-active' : ''}`} data-testid="filter-all"><LayoutGrid className="w-3 h-3 inline mr-1" /> All</button>
              {categories.map((cat) => (
                <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`tech-chip ${selectedCategory === cat.id ? 'tech-chip-active' : ''}`} data-testid={`filter-${cat.name.toLowerCase().replace(/\s/g, '-')}`}>{cat.name}</button>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="py-4 pb-16">
            {(activeTab === 'logs' || activeTab === 'experiments' || activeTab === 'saved') && (
              <LogGrid
                items={activeTab === 'logs' ? logs : activeTab === 'experiments' ? experiments : savedLogs}
                saved={saved} onOpen={(id) => navigate(`/article/${id}`)} onSave={toggleSave}
                emptyText={activeTab === 'saved' ? 'No archived logs yet. Bookmark a research log to store it here.' : 'No research logs in this domain.'}
              />
            )}

            {activeTab === 'vault' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blueprints-grid">
                {byCat(pdfs).length === 0 ? <EmptyState text="No blueprints archived in this domain." /> : byCat(pdfs).map((pdf) => (
                  <div key={pdf.id} className="r-card p-6 flex flex-col" data-testid={`blueprint-${pdf.id}`}>
                    <div className="flex justify-between items-start mb-5">
                      <div className="w-11 h-11 rounded-lg border border-[#262A36] bg-[#0c0f17] flex items-center justify-center"><FileText className="w-5 h-5 text-[#26B0A8]" /></div>
                      <span className="tag tag-teal">Blueprint</span>
                    </div>
                    <h3 className="font-serif-display text-xl font-medium mb-2">{pdf.title}</h3>
                    <p className="font-sans-tech text-sm text-[#949BAA] leading-relaxed flex-1">{pdf.description}</p>
                    <div className="flex items-center justify-between mt-5">
                      <span className="mono-meta flex items-center gap-2">{pdf.file_size ? `PDF · ${pdf.file_size}` : 'PDF · classified'}{pdf.evidence_class && <EvidenceLabel evidenceClass={pdf.evidence_class} />}</span>
                      <button onClick={() => handleRetrieve(pdf)} className="btn-ambient !py-2 !px-4 !text-[0.66rem]" data-testid={`download-${pdf.id}`}><Download className="w-4 h-4" /> Retrieve</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeTab === 'vault' && (
              <p className="mono-meta text-[#5b626f] mt-8 pt-6 border-t border-[#262A36]/60" data-testid="vault-contact-note">
                Trouble retrieving a blueprint, or need a specific schematic? Research Contact · <a href="mailto:research@freefieldflow.com" className="text-[#26B0A8] hover:text-[#34d3ca] transition-colors">research@freefieldflow.com</a>
              </p>
            )}

            {activeTab === 'updates' && (
              <div className="max-w-3xl" data-testid="updates-list">
                {updates.length === 0 ? <EmptyState text="No field transmissions logged." /> : (
                  <div className="space-y-8">
                    {updates.map((u) => (
                      <div key={u.id} className="transmission" data-testid={`update-${u.id}`}>
                        <span className="transmission-dot" />
                        <p className="mono-meta text-[#1E8F89] mb-1">{new Date(u.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} · TRANSMISSION</p>
                        <h3 className="font-serif-display text-2xl font-light">{u.title}</h3>
                        <p className="font-sans-tech text-[#949BAA] mt-2 leading-relaxed">{u.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* SYSTEM STATUS PANEL */}
        <aside className="min-w-0">
          <div className="xl:sticky xl:top-24 space-y-5">
            <BillingPanel />
            <div className="glass-hud rounded-xl p-5 reflect-top">
              <p className="mono-label mb-4 flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> System Status</p>
              <div className="space-y-3 font-sans-tech text-sm">
                <Row label="Archive" value="Online" ok />
                <Row label="Signal" value="Nominal" ok />
                <Row label="Active domain" value={activeCategory ? activeCategory.name : 'All'} />
                <Row label="Saved" value={`${saved.length} logs`} />
              </div>
            </div>
            <div className="glass-hud rounded-xl p-5">
              <p className="mono-label mb-4 flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> Archive Counters</p>
              <div className="grid grid-cols-2 gap-3">
                <Counter label="Logs" val={articles.length} />
                <Counter label="Blueprints" val={pdfs.length} />
                <Counter label="Experiments" val={experiments.length} />
                <Counter label="Updates" val={updates.length} />
              </div>
            </div>
            <div className="glass-hud rounded-xl p-5">
              <p className="mono-label mb-4 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Recent Activity</p>
              <div className="space-y-3">
                {articles.slice(0, 3).map((a) => (
                  <button key={a.id} onClick={() => navigate(`/article/${a.id}`)} className="block text-left w-full group" data-testid={`recent-${a.id}`}>
                    <p className="font-sans-tech text-sm text-[#AEB6C4] group-hover:text-[#26B0A8] transition-colors line-clamp-2">{a.title}</p>
                    <p className="mono-meta mt-1">{readTime(a.content)} min read</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const Row = ({ label, value, ok }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#949BAA]">{label}</span>
    <span className={`flex items-center gap-2 ${ok ? 'text-[#26B0A8]' : 'text-[#E2E4E9]'}`}>
      {ok && <span className="w-1.5 h-1.5 rounded-full bg-[#1E8F89] animate-pulse" />}{value}
    </span>
  </div>
);

const fmtBillingDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusLabel = (s) => {
  const m = {
    active: 'Active', trialing: 'Trialing', past_due: 'Past due',
    canceled: 'Cancelled', cancelled: 'Cancelled', unpaid: 'Unpaid', inactive: 'Inactive',
  };
  return m[(s || '').toLowerCase()] || (s || '—');
};

// Read-only member billing summary for Research Access. No mutation controls.
const BillingPanel = () => {
  const [b, setB] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/me/billing`).then((r) => { if (alive) setB(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!b) return null;
  const renewal = fmtBillingDate(b.renewal_date);
  const started = fmtBillingDate(b.started_at);
  const NA = <span className="text-[#4a4a52] italic">Managed in Stripe</span>;
  const rows = [
    ['Plan', b.plan],
    ['Price', b.price],
    ['Status', b.tier === 'admin'
      ? <span className="tag tag-violet">Admin · permanent</span>
      : <span className={`tag ${b.has_access ? 'tag-teal' : 'tag-muted'}`}>{b.tier === 'admin' ? 'Admin' : statusLabel(b.status)}</span>],
  ];
  if (b.tier !== 'admin' && b.tier !== 'free') {
    rows.push(['Next renewal', renewal || NA]);
    rows.push(['Cancels at period end', b.cancel_at_period_end == null ? NA : (b.cancel_at_period_end ? 'Yes' : 'No')]);
    if (started) rows.push(['Started', started]);
  }
  return (
    <div className="glass-hud rounded-xl p-5 reflect-top" data-testid="billing-panel">
      <p className="mono-label mb-4 flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" /> Research Access</p>
      <div className="space-y-3 font-sans-tech text-sm">
        {rows.map(([label, val], i) => (
          <div key={i} className="flex items-center justify-between gap-3" data-testid={`billing-row-${String(label).toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <span className="text-[#949BAA] shrink-0">{label}</span>
            <span className="text-right text-[#E2E4E9]">{val}</span>
          </div>
        ))}
      </div>
      {b.tier !== 'admin' && (
        <p className="mono-meta text-[#5b626f] mt-4 pt-3 border-t border-[#262A36] leading-relaxed">Billing is managed securely by Stripe. This is a read-only summary.</p>
      )}
    </div>
  );
};

const Counter = ({ label, val }) => (
  <div className="border border-[#262A36] rounded-lg p-3">
    <div className="font-serif-display text-3xl font-light text-white">{val}</div>
    <div className="mono-meta mt-1">{label}</div>
  </div>
);

const LogGrid = ({ items, saved, onOpen, onSave, emptyText }) => {
  if (!items || items.length === 0) return <EmptyState text={emptyText} />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="logs-grid">
      {items.map((a) => {
        const tag = statusTag(a.status);
        const isSaved = saved.includes(a.id);
        return (
          <div key={a.id} className="r-card p-6 flex flex-col group" data-testid={`log-${a.id}`}>
            <div className="flex justify-between items-start mb-4">
              <span className={`tag ${tag.cls}`}>{tag.label}</span>
              <button onClick={(e) => { e.stopPropagation(); onSave(a.id); }} className="text-[#6B7280] hover:text-[#26B0A8] transition-colors" data-testid={`save-${a.id}`} aria-label="Save log">
                {isSaved ? <BookmarkCheck className="w-5 h-5 text-[#26B0A8]" /> : <Bookmark className="w-5 h-5" />}
              </button>
            </div>
            <p className="mono-meta mb-2">{a.content_type || 'Field Note'}{a.experiment_phase ? ` · ${a.experiment_phase}` : ''}</p>
            <h3 className="font-serif-display text-xl font-medium mb-2 group-hover:text-[#26B0A8] transition-colors">{a.title}</h3>
            <p className="font-sans-tech text-sm text-[#949BAA] leading-relaxed flex-1">{a.description}</p>
            {a.evidence_class && <div className="mt-3"><EvidenceLabel evidenceClass={a.evidence_class} testid={`log-evidence-${a.id}`} /></div>}
            <div className="flex items-center justify-between mt-5">
              <span className="mono-meta">{readTime(a.content)} min read</span>
              <button onClick={() => onOpen(a.id)} className="flex items-center gap-2 text-[#26B0A8] text-xs font-mono-tech uppercase tracking-widest" data-testid={`open-${a.id}`}>Open <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div className="col-span-full py-24 text-center glass-panel rounded-xl"><p className="font-sans-tech text-[#6B7280]">{text}</p></div>
);

const RESEARCH_ACCESS_UNLOCKS = [
  'Full archive — every field log, summary & source trail',
  'Blueprint Vault — downloadable PDF documents & schematics',
  'Comparative reviews & curated research synthesis',
  'Measurement tables, materials lists & build specifications',
  'Documented experiments, failure modes & practical limits',
  'Full semantic traversal of the connected archive',
];

const ResearchAccessPanel = ({ onSubscribe, stats }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
    className="mt-10 glass-hud rounded-2xl overflow-hidden reflect-top scanlines" data-testid="research-access-panel"
  >
    <div className="grid md:grid-cols-[1.3fr_1fr]">
      <div className="p-8 lg:p-10 border-b md:border-b-0 md:border-r border-[#262A36]">
        <p className="mono-label mb-4 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[#26B0A8]" /> Research Access · Membership</p>
        <h2 className="font-serif-display text-3xl lg:text-4xl font-light mb-5">Access the full research network</h2>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {RESEARCH_ACCESS_UNLOCKS.map((f) => (
            <div key={f} className="flex items-start gap-2.5">
              <Check className="w-4 h-4 text-[#26B0A8] shrink-0 mt-0.5" />
              <span className="font-sans-tech text-sm text-[#C8CDD6] leading-snug">{f}</span>
            </div>
          ))}
        </div>
        <ArchiveDepthBar stats={stats} />
        <p className="mono-meta text-[#5b626f] mt-5 leading-relaxed">
          Records are labelled <span className="text-[#8a93a3]">source synthesis</span> (grounded summaries of third-party
          literature) or <span className="text-[#8a93a3]">field work</span> where FreeFieldFlow performs original measurement.
          Every record carries a visible evidence class. <button onClick={() => (window.location.href = '/method')} className="text-[#26B0A8] hover:text-[#34d3ca] transition-colors" data-testid="panel-method-link">How the archive classifies evidence →</button>
        </p>
      </div>
      <div className="p-8 lg:p-10 flex flex-col justify-center bg-[#0a0c12]">
        <div className="flex items-end gap-2 mb-1">
          <span className="font-serif-display text-5xl font-light text-white">€9</span>
          <span className="text-[#949BAA] mb-2 font-sans-tech">/ month</span>
        </div>
        <p className="mono-meta mb-6">Instant access · Cancel anytime</p>
        <button onClick={onSubscribe} className="btn-primary w-full" data-testid="subscribe-button">Unlock Research Access <ArrowRight className="w-4 h-4" /></button>
        <p className="mono-meta text-center mt-4 text-[#5b626f]">Secure payment via Stripe</p>
      </div>
    </div>
  </motion.div>
);

const TeaserGrid = ({ items, onOpen, emptyText }) => {
  if (!items || items.length === 0) return <EmptyState text={emptyText} />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="logs-grid">
      {items.map((a) => {
        const tag = statusTag(a.status);
        const locked = a.locked;
        return (
          <div key={a.id} onClick={() => onOpen(a.id)} className="r-card p-6 flex flex-col group cursor-pointer relative" data-testid={`teaser-${a.id}`}>
            <div className="flex justify-between items-start mb-4">
              <span className={`tag ${tag.cls}`}>{tag.label}</span>
              {locked
                ? <span className="tag tag-muted flex items-center gap-1" data-testid={`locked-${a.id}`}><Lock className="w-3 h-3" /> Restricted</span>
                : <span className="tag tag-teal flex items-center gap-1" data-testid={`public-${a.id}`}><BookOpen className="w-3 h-3" /> Public</span>}
            </div>
            <p className="mono-meta mb-2">{a.content_type || 'Field Note'}{a.experiment_phase ? ` · ${a.experiment_phase}` : ''}</p>
            <h3 className="font-serif-display text-xl font-medium mb-2 group-hover:text-[#26B0A8] transition-colors">{a.title}</h3>
            <p className="font-sans-tech text-sm text-[#949BAA] leading-relaxed flex-1">{a.description}</p>
            {a.evidence_class && <div className="mt-3"><EvidenceLabel evidenceClass={a.evidence_class} testid={`teaser-evidence-${a.id}`} /></div>}
            {locked && (
              <div className="mt-3" data-testid={`redacted-${a.id}`}>
                <div className="flex items-center gap-2 mb-2" aria-hidden="true">
                  <span className="mono-meta text-[#5b626f] whitespace-nowrap">Internal field record</span>
                  <span className="redact flex-1" />
                </div>
                {a.excerpt && (
                  <div className="relative max-h-14 overflow-hidden">
                    <p className="font-mono-tech text-[0.72rem] text-[#5b626f] leading-relaxed blur-[1.8px] select-none">{a.excerpt}</p>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0c0f17] via-[#0c0f17]/40 to-transparent" />
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between mt-5">
              {locked
                ? <span className="mono-meta flex items-center gap-1.5 text-[#6B7280]"><Lock className="w-3 h-3" /> Research Access Required</span>
                : <span className="mono-meta">{readTime(a.content)} min read</span>}
              <span className="flex items-center gap-1.5 text-[#26B0A8] text-xs font-mono-tech uppercase tracking-widest">{locked ? 'Preview' : 'Open'} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Dashboard;
